import { useState, useRef, useEffect } from "react";
import { Input } from "./ui/input";

/**
 * Email input with autocomplete from contacts and transaction people.
 * Value is a comma-separated string of emails.
 */
export const EmailAutocomplete = ({ value, onChange, contacts = [], transactionPeople = [], placeholder, className, ...props }) => {
  const [query, setQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Build searchable list: transaction people + org contacts (deduplicated by email)
  const allSuggestions = (() => {
    const seen = new Set();
    const list = [];
    for (const p of transactionPeople) {
      if (p.email && !seen.has(p.email.toLowerCase())) {
        seen.add(p.email.toLowerCase());
        list.push({ name: p.name || "", email: p.email, source: "transaction" });
      }
    }
    for (const c of contacts) {
      if (c.email && !seen.has(c.email.toLowerCase())) {
        seen.add(c.email.toLowerCase());
        list.push({ name: c.name || "", email: c.email, source: "contact" });
      }
    }
    return list;
  })();

  // Already-entered emails
  const existingEmails = (value || "").split(",").map(e => e.trim().toLowerCase()).filter(Boolean);

  // Filter suggestions by current query segment (the part after the last comma)
  const currentSegment = query.toLowerCase().trim();
  const filtered = currentSegment.length > 0
    ? allSuggestions.filter(s =>
        !existingEmails.includes(s.email.toLowerCase()) &&
        (s.name.toLowerCase().includes(currentSegment) || s.email.toLowerCase().includes(currentSegment))
      )
    : [];

  useEffect(() => { setSelectedIndex(0); }, [currentSegment]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectSuggestion = (suggestion) => {
    const parts = (value || "").split(",").map(e => e.trim()).filter(Boolean);
    parts.push(suggestion.email);
    onChange(parts.join(", "));
    setQuery("");
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || filtered.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === "Enter" || e.key === "Tab") {
      if (filtered[selectedIndex]) {
        e.preventDefault();
        selectSuggestion(filtered[selectedIndex]);
      }
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const handleInputChange = (e) => {
    const raw = e.target.value;
    // Split by comma — committed emails go to value, last segment is the active query
    const parts = raw.split(",");
    const lastPart = parts.pop().trim();
    const committed = parts.map(p => p.trim()).filter(Boolean);

    if (committed.length > 0) {
      const existing = (value || "").split(",").map(p => p.trim()).filter(Boolean);
      const merged = [...new Set([...existing, ...committed])];
      onChange(merged.join(", "));
    }
    setQuery(lastPart);
    setShowSuggestions(lastPart.length > 0);
  };

  // Display: the committed value + current query
  const displayValue = (() => {
    const base = (value || "").split(",").map(e => e.trim()).filter(Boolean);
    if (query) return base.length > 0 ? base.join(", ") + ", " + query : query;
    return base.join(", ");
  })();

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        ref={inputRef}
        value={displayValue}
        onChange={handleInputChange}
        onFocus={() => { if (query.length > 0) setShowSuggestions(true); }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={className}
        data-testid={props["data-testid"]}
      />
      {showSuggestions && filtered.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-[var(--t-border)] rounded-md shadow-lg max-h-48 overflow-y-auto" data-testid="email-autocomplete-suggestions">
          {filtered.slice(0, 8).map((s, i) => (
            <button
              key={s.email}
              type="button"
              className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-slate-50 transition-colors ${i === selectedIndex ? "bg-slate-100" : ""}`}
              onMouseDown={(e) => { e.preventDefault(); selectSuggestion(s); }}
              data-testid={`email-suggestion-${i}`}
            >
              <div className="flex-1 min-w-0">
                <span className="font-medium text-[var(--t-text)] truncate block">{s.name || s.email}</span>
                {s.name && <span className="text-xs text-[var(--t-text-muted)] truncate block">{s.email}</span>}
              </div>
              <span className="text-[10px] text-[var(--t-text-subtle)] shrink-0 uppercase">
                {s.source === "transaction" ? "Txn" : "Contact"}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
